import React from 'react';
import { Article } from '../../../../../models/article';
import Spacer from '../../../../../components/presentational/Spacer';
import { compose } from 'recompose';
import cardBlockStyles from './CardBlock_styles';
import styled, { StylesProps } from '../../../../../lib/styles';
import Holiday from '../../../../../components/presentational/Holiday';
import { Holiday as HolidayModel } from '../../../../../models/holiday';
import { trackEvent } from '../../../../../lib/ga';
import { CARD_BLOCK_CLICKED } from '../../../lib/ga';
import Typography from '../../../../../components/presentational/Typography';
import ArticleCard from './Cards/ArticleCard/ArticleCard';
import CustomCard from './Cards/CustomCard/CustomCard';
import FeaturedHoliday from '../../../../../components/presentational/Holiday/FeaturedHoliday';
import ArticleFeature from './Features/ArticleFeature/ArticleFeature';
import CustomFeature from './Features/CustomFeature/CustomFeature';
import Button from '../../../../../components/presentational/Button';

interface ArticleCard {
  type: 'article_card';
  article: Article;
  disableAuthor: boolean;
}

interface HolidayCard {
  type: 'holiday_card';
  holiday: HolidayModel;
}

export interface CustomCard {
  type: 'custom_card';
  category: string;
  highlight: string;
  hoverText: string;
  image: string;
  mobile_image: string;
  link: string;
  text: string;
  title: string;
  ribbonText: string;
}

type Card = ArticleCard | HolidayCard | CustomCard;

export interface CardBlockData {
  type: 'card_block';
  title: string;
  description: string;
  subtitle: string;
  cards: Card[];
  feature: Card;
  logo: string;
  backgroundColor?: string;
  moreButtonText?: string;
  moreButtonURL?: string;
}

export interface CardBlockProps {
  block: CardBlockData;
  backgroundColor: string | undefined;
  lazy?: boolean;
}
const renderHolidayCard = (
  card: HolidayCard,
  cardIndex: number,
  onClick?: () => any
) => {
  return <Holiday key={cardIndex} holiday={card.holiday} onClick={onClick} />;
};

const renderArticleCard = (
  card: ArticleCard,
  cardIndex: number,
  lazy: boolean,
  onClick?: () => any
) => {
  return (
    <ArticleCard
      article={card.article}
      key={cardIndex}
      disableAuthor={card.disableAuthor}
      lazy={lazy}
      onClick={onClick}
    />
  );
};

const renderCustomCard = (
  card: CustomCard,
  cardIndex: number,
  onClick?: () => any
) => {
  return <CustomCard card={card} key={cardIndex} onClick={onClick} />;
};

const renderCard = (
  card: Card,
  lazy: boolean,
  cardIndex: number,
  onClick?: () => any
) => {
  switch (card.type) {
    case 'holiday_card':
      return card.holiday && renderHolidayCard(card, cardIndex, onClick);
    case 'article_card':
      return renderArticleCard(card, cardIndex, lazy, onClick);
    case 'custom_card':
      return renderCustomCard(card, cardIndex, onClick);
  }
};

const renderFeature = (feature: Card, lazy: boolean, onClick?: () => any) => {
  switch (feature.type) {
    case 'holiday_card':
      return (
        feature.holiday && (
          <FeaturedHoliday
            holiday={feature.holiday}
            lazy={lazy}
            onClick={onClick}
          />
        )
      );
    case 'article_card':
      return (
        <ArticleFeature
          article={feature.article}
          disableAuthor={feature.disableAuthor}
          lazy={lazy}
          onClick={onClick}
        />
      );
    case 'custom_card':
      return <CustomFeature card={feature} lazy={lazy} onClick={onClick} />;
  }
};

const CardBlock = ({
  block: {
    cards,
    title,
    subtitle,
    description,
    feature,
    logo,
    moreButtonText,
    moreButtonURL,
  },
  lazy = true,
  styles,
}: CardBlockProps & StylesProps<typeof cardBlockStyles>) => (
  <div className={`${styles.container} card-block`}>
    <Spacer width={1160}>
      <Spacer space={'m'}>
        {(title || subtitle || description) && (
          <Spacer space={'xs'} width={903}>
            {logo && <img src={logo} className={styles.logo} />}
            <Spacer space={'xs'}>
              {subtitle && (
                <Typography weight={'normal'}>
                  <h3>{subtitle}</h3>
                </Typography>
              )}
              {title && (
                <Typography>
                  <h1>{title}</h1>
                </Typography>
              )}
            </Spacer>
            {description && (
              <Typography weight={'normal'}>
                <h3>{description}</h3>
              </Typography>
            )}
          </Spacer>
        )}

        <Spacer space={'s'}>
          {feature &&
            renderFeature(feature, lazy, () =>
              trackEvent(
                { ...CARD_BLOCK_CLICKED, label: 'feature' },
                { transport: 'beacon' }
              )
            )}

          <div className={styles.cards}>
            {cards
              .filter(card => card)
              .map((card: Card, cardIndex: number) => (
                <div key={cardIndex} className={styles.card}>
                  {renderCard(card, lazy, cardIndex, () =>
                    trackEvent(CARD_BLOCK_CLICKED, { transport: 'beacon' })
                  )}
                </div>
              ))}
          </div>
        </Spacer>

        {moreButtonText && moreButtonURL && (
          <Spacer align={'center'}>
            <Button href={moreButtonURL}>{moreButtonText}</Button>
          </Spacer>
        )}
      </Spacer>
    </Spacer>
  </div>
);

export default compose(styled(cardBlockStyles))(CardBlock);
